import { useState, useEffect } from 'react';
import { NavLink, useLocation, Location } from 'react-router-dom';

// services
import { getUserAuth } from '@/services/storage/user-auth.service';

// styles
import './style.css';

// material ui
import { Divider, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, SvgIconTypeMap } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { IconType } from 'react-icons';

// enums
import { UserType } from '@/shared/enums';

// metadata
import { adminSidebar, cashierSidebar, auditorSidebar } from './metadata';

const openedMixin = (theme: Theme): CSSObject => ({
  width: 240,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  width: 240,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
      },
    },
  ],
}));

interface IProps {
  isSidebarCollapsed: boolean;
}

export interface ISidebarLink {
  title: string;
  icon: IconType | (OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & { muiName: string });
  path: string;
  iconFontSize: number;
}

export interface ISidebar {
  title: string;
  routes: ISidebarLink[];
}

export const Sidebar = ({ isSidebarCollapsed }: IProps) => {
  const location: Location = useLocation();
  const routepath = location.pathname.split('/');

  const [currentRoutes, setCurrentRoutes] = useState<ISidebar[]>(adminSidebar);

  useEffect(() => {
    const authData = getUserAuth();
    if (authData && authData.userType === UserType.CASHIER) {
      setCurrentRoutes(cashierSidebar);
    } else if (authData && authData.userType === UserType.ADMIN) {
      setCurrentRoutes(adminSidebar);
    } else if (authData && authData.userType === UserType.AUDITOR) {
      setCurrentRoutes(auditorSidebar);
    }
  }, []);

  return (
    <>
      <Drawer variant="permanent" open={!isSidebarCollapsed} className="sidebar">
        {currentRoutes.map((nestedMenus, index) => (
          <>
            <Divider />
            <List key={index} subheader={!isSidebarCollapsed && <ListSubheader>Management</ListSubheader>}>
              {nestedMenus.routes.map((route, index2) => (
                <>
                  <NavLink key={index2} to={route.path}>
                    <ListItemButton
                      selected={routepath[2] === route.path}
                      className={`${isSidebarCollapsed && 'sidebarCollapsed'} ${
                        routepath[2] === route.path && 'sidebarActive'
                      } sidebarMenu`}>
                      <ListItemIcon sx={{ fontSize: 22, minWidth: '40px !important' }}>
                        <route.icon />
                      </ListItemIcon>
                      <ListItemText primary={route.title} />
                    </ListItemButton>
                  </NavLink>
                </>
              ))}
            </List>
          </>
        ))}
      </Drawer>
    </>
  );
};
